/** Commercial AI Assistant — domain types (Sprint 1.6) */

export type AILanguage = 'pt-BR' | 'en-US' | 'es-ES'

export type AIHistoryType =
  | 'analyze_quote'
  | 'analyze_purchase'
  | 'recommend_supplier'
  | 'recommend_price'
  | 'generate_description'
  | 'generate_observations'
  | 'summarize'
  | 'generate_insights'

export type RecommendationKind =
  | 'supplier'
  | 'price'
  | 'profit'
  | 'margin'
  | 'delivery'
  | 'discount'
  | 'general'

export type InsightSeverity = 'info' | 'warning' | 'danger' | 'opportunity'

export interface AIConfig {
  model: string
  temperature: number
  maxTokens: number
  language: AILanguage
}

export const DEFAULT_AI_CONFIG: AIConfig = {
  model: 'masterhub-commercial-local-v1',
  temperature: 0.3,
  maxTokens: 1200,
  language: 'pt-BR',
}

export interface AIRecommendation {
  id: string
  kind: RecommendationKind
  title: string
  suggestion: string
  reason: string
  confidence: number
  impact?: string
  requiresConfirmation: true
}

export interface AIAlert {
  id: string
  code: string
  severity: InsightSeverity
  title: string
  message: string
  reason: string
}

export interface AIInsightBundle {
  alerts: AIAlert[]
  opportunities: AIAlert[]
  risks: AIAlert[]
  executiveSummary: string
  potentialEconomy: number
  recommendedSupplier: string | null
  suggestedMargin: number | null
}

export interface PricingAdvice {
  idealPrice: number
  idealMargin: number
  maxDiscountPercent: number
  reason: string
  recommendations: AIRecommendation[]
}

export interface SupplierAdvice {
  supplier: string
  score: number
  reason: string
  priceSignal: number
  deliverySignal: number
  reliabilitySignal: number
  recommendations: AIRecommendation[]
}

export interface QuoteAnalysisItemInput {
  id: string
  description: string
  quantity: number
  purchasePrice: number
  salePrice: number
  margin: number
  profit: number
  stock?: number
  supplier?: string
  marketMinPrice?: number
  marketMaxPrice?: number
}

export interface QuoteAnalysisInput {
  quoteId?: string
  items: QuoteAnalysisItemInput[]
  targetMargin?: number
}

export interface QuoteAnalysisResult {
  totalValue: number
  averageMargin: number
  recommendedSupplier: string | null
  risks: AIAlert[]
  recommendations: AIRecommendation[]
  lowMarginItems: string[]
  negativeProfitItems: string[]
  outOfStockItems: string[]
  highVarianceItems: string[]
  summary: string
}

export interface PurchaseAnalysisInput {
  items: Array<{
    product: string
    supplier: string
    unitPrice: number
    quantity: number
    deliveryDays: number
  }>
  totalCost?: number
  freightCost?: number
}

export interface PurchaseAnalysisResult {
  summary: string
  recommendations: AIRecommendation[]
  potentialEconomy: number
  preferredSuppliers: string[]
}

export interface TechnicalDescriptionResult {
  description: string
  diagnosis: string
  solution: string
  observations: string
  reason: string
}

export interface CommercialObservationsResult {
  prazo: string
  garantia: string
  condicoes: string
  validade: string
  fullText: string
  reason: string
}

export interface AIStructuredResponse<T> {
  ok: true
  type: AIHistoryType
  data: T
  model: string
  language: AILanguage
  generatedAt: string
  disclaimer: string
}

export interface AIHistoryEntry {
  id: string
  type: AIHistoryType
  request: string
  response: string
  createdAt: string
}

export const AI_DISCLAIMER =
  'Recomendação da IA Comercial — nenhuma alteração é aplicada automaticamente. Confirme antes de mudar o orçamento.'
